import { useState, useEffect, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { Briefcase, Edit, FileText, Filter, Plus, Scale, Search } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { CopyableProcessNumber } from '@/components/ui/CopyableProcessNumber'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { NativeSelect } from '@/components/ui/native-select'
import { Pagination } from '@/components/ui/Pagination'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { CaseDeadlineHint } from '@/components/cases/CaseBadges'
import { CaseArchiveSwitch, CaseStatusControl } from '@/components/cases/CaseStatusControl'
import { cn } from '@/utils/cn'
import {
  Case,
  CaseFilters,
  PaginationOptions,
  CaseStatus,
  CaseArea,
  CasePriority,
  CaseStatusLabels,
  CaseAreaLabels,
  CasePriorityLabels
} from '../types/case'
import { caseService } from '../services/case-service'
import { getErrorMessage } from '../utils/error.utils'

type CasesView = 'todos' | 'processos' | 'sem-processo'

export function CasesPage() {
  const [cases, setCases] = useState<Case[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [filters, setFilters] = useState<CaseFilters>({})
  const [pagination, setPagination] = useState<PaginationOptions>({
    page: 1,
    limit: 20,
    sort: 'createdAt:desc'
  })
  const [totalPages, setTotalPages] = useState(0)
  const [totalCases, setTotalCases] = useState(0)
  const [showFilters, setShowFilters] = useState(false)

  const loadCases = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await caseService.getAllCases(filters, pagination)
      setCases(response.cases)
      setTotalPages(response.pagination.totalPages)
      setTotalCases(response.pagination.total)
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Erro ao carregar casos'))
    } finally {
      setLoading(false)
    }
  }, [filters, pagination])

  useEffect(() => {
    loadCases()
  }, [loadCases])

  const handleSearch = (searchTerm: string) => {
    setFilters({ ...filters, search: searchTerm })
    setPagination({ ...pagination, page: 1 })
  }

  const handleFilterChange = (newFilters: Partial<CaseFilters>) => {
    setFilters({ ...filters, ...newFilters })
    setPagination({ ...pagination, page: 1 })
  }

  const clearFilters = () => {
    setFilters({})
    setPagination({ ...pagination, page: 1 })
  }

  const handlePageChange = (page: number) => {
    setPagination({ ...pagination, page })
  }

  // A API devolve o caso sem o cliente expandido: mantém o que já estava na lista.
  const replaceCase = (updated: Case) =>
    setCases((prev) => prev.map((c) => (c.id === updated.id ? { ...c, ...updated, client: updated.client ?? c.client } : c)))

  const activeFilterCount = Object.keys(filters).filter(k => k !== 'onlyLegalProcesses' && k !== 'excludeLegalProcesses').length
  const hasFilters = Object.keys(filters).length > 0
  const view: CasesView = filters.onlyLegalProcesses ? 'processos' : filters.excludeLegalProcesses ? 'sem-processo' : 'todos'

  const changeView = (next: CasesView) => {
    const newFilters = { ...filters }
    delete newFilters.onlyLegalProcesses
    delete newFilters.excludeLegalProcesses
    if (next === 'processos') newFilters.onlyLegalProcesses = true
    if (next === 'sem-processo') newFilters.excludeLegalProcesses = true
    setFilters(newFilters)
    setPagination({ ...pagination, page: 1 })
  }

  return (
    <div className="flex flex-col gap-4 p-4 sm:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 flex-col gap-1">
          <div className="flex flex-wrap items-center gap-2">
            <Scale className="size-6 text-muted-foreground" />
            <h1 className="text-2xl font-semibold">Casos</h1>
            <Badge variant="secondary">{totalCases} {totalCases === 1 ? 'caso' : 'casos'}</Badge>
          </div>
          <p className="text-sm text-muted-foreground">
            Todos os casos do escritório. Para trabalhar num cliente, abra o dossiê dele em Clientes.
          </p>
        </div>
        <Button asChild>
          <Link to="/cases/new"><Plus className="size-4" /> Novo Caso</Link>
        </Button>
      </div>

      {error && (
        <div role="alert" className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm">
          <div>
            <p className="font-medium text-destructive">Erro ao carregar casos</p>
            <p className="text-muted-foreground">{error}</p>
          </div>
          <Button variant="outline" size="sm" onClick={() => loadCases()}>Tentar novamente</Button>
        </div>
      )}

      <div className="flex flex-col gap-3">
        <Tabs value={view} onValueChange={(next) => changeView(next as CasesView)}>
          <TabsList className="h-auto flex-wrap">
            <TabsTrigger value="todos">Todos</TabsTrigger>
            <TabsTrigger value="processos"><Scale className="size-4" /> Apenas Processos Judiciais</TabsTrigger>
            <TabsTrigger value="sem-processo"><Briefcase className="size-4" /> Apenas Casos sem Processo</TabsTrigger>
          </TabsList>
        </Tabs>

        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
            <Input
              type="text"
              aria-label="Buscar casos"
              placeholder="Buscar casos..."
              value={filters.search || ''}
              onChange={(e) => handleSearch(e.target.value)}
              className="pl-8"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => setShowFilters(!showFilters)} aria-expanded={showFilters}>
              <Filter className="size-4" />
              <span className="hidden sm:inline">Filtros Avançados</span>
              <span className="sm:hidden">Filtros</span>
              {activeFilterCount > 0 && <Badge variant="secondary">{activeFilterCount}</Badge>}
            </Button>
            {hasFilters && <Button variant="ghost" onClick={clearFilters}>Limpar</Button>}
          </div>
        </div>

        {showFilters && (
          <Card size="sm">
            <CardContent className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="cases-filter-status">Status</Label>
                <NativeSelect
                  id="cases-filter-status"
                  value={filters.status?.[0] || ''}
                  onChange={(e) => handleFilterChange({ status: e.target.value ? [e.target.value as CaseStatus] : undefined })}
                >
                  <option value="">Todos os status</option>
                  {Object.entries(CaseStatusLabels)
                    .filter(([value]) => value !== 'CONVERTED_TO_LEGAL')
                    .map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                </NativeSelect>
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="cases-filter-area">Área</Label>
                <NativeSelect
                  id="cases-filter-area"
                  value={filters.area?.[0] || ''}
                  onChange={(e) => handleFilterChange({ area: e.target.value ? [e.target.value as CaseArea] : undefined })}
                >
                  <option value="">Todas as áreas</option>
                  {Object.entries(CaseAreaLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                </NativeSelect>
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="cases-filter-priority">Prioridade</Label>
                <NativeSelect
                  id="cases-filter-priority"
                  value={filters.priority?.[0] || ''}
                  onChange={(e) => handleFilterChange({ priority: e.target.value ? [e.target.value as CasePriority] : undefined })}
                >
                  <option value="">Todas as prioridades</option>
                  {Object.entries(CasePriorityLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                </NativeSelect>
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="cases-filter-min-value">Valor Mínimo</Label>
                <Input
                  id="cases-filter-min-value"
                  type="number"
                  placeholder="R$ 0,00"
                  value={filters.estimatedValueMin || ''}
                  onChange={(e) => handleFilterChange({ estimatedValueMin: e.target.value ? parseFloat(e.target.value) : undefined })}
                />
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      <Card className="py-0">
        {loading ? (
          <div className="flex flex-col gap-2 p-4" aria-busy="true">
            <span className="sr-only">Carregando casos...</span>
            {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-16" />)}
          </div>
        ) : cases.length === 0 ? (
          <div className="flex flex-col items-center gap-2 px-4 py-10 text-center">
            <FileText className="size-10 text-muted-foreground" aria-hidden="true" />
            <h3 className="text-sm font-medium">Nenhum caso encontrado</h3>
            <p className="text-sm text-muted-foreground">
              {hasFilters ? 'Tente ajustar os filtros de busca.' : 'Comece criando seu primeiro caso.'}
            </p>
            {!hasFilters && (
              <Button asChild className="mt-2">
                <Link to="/cases/new"><Plus className="size-4" /> Novo Caso</Link>
              </Button>
            )}
          </div>
        ) : (
          <ul className="divide-y">
            {cases.map((case_) => (
              <li
                key={case_.id}
                className={cn('flex flex-col hover:bg-muted/60 sm:flex-row sm:items-center', case_.processNumber && 'border-l-2 border-l-primary')}
              >
                <Link to={`/cases/${case_.id}`} className="min-w-0 flex-1 px-4 py-3">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="truncate text-sm font-medium">{case_.title}</p>
                        {case_.processNumber && (
                          <Badge variant="outline"><Scale aria-hidden="true" /> Processo Judicial</Badge>
                        )}
                      </div>
                      {case_.processNumber && (
                        <div className="mt-1">
                          <CopyableProcessNumber number={case_.processNumber} className="text-xs text-muted-foreground" />
                        </div>
                      )}
                      <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-muted-foreground">
                        <span className="truncate">{case_.client?.name || 'Cliente não informado'}</span>
                        <span className="hidden sm:inline" aria-hidden="true">•</span>
                        <span>{CaseAreaLabels[case_.area]}</span>
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-3 sm:flex-col sm:items-end sm:gap-2">
                      <CaseStatusControl caseId={case_.id} caseTitle={case_.title} status={case_.status} onChanged={replaceCase} />
                      <CaseArchiveSwitch caseId={case_.id} caseTitle={case_.title} status={case_.status} onChanged={replaceCase} />
                      <CaseDeadlineHint expectedEndDate={case_.expectedEndDate} />
                    </div>
                  </div>
                </Link>
                <div className="flex items-center justify-end gap-1 px-3 pb-2 sm:pb-0">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button variant="ghost" size="icon" asChild>
                        <Link to={`/cases/${case_.id}/edit`} aria-label="Editar caso" onClick={(e) => e.stopPropagation()}>
                          <Edit className="size-4" />
                        </Link>
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Editar caso</TooltipContent>
                  </Tooltip>
                </div>
              </li>
            ))}
          </ul>
        )}

        {totalPages > 1 && (
          <div className="border-t px-4 py-3">
            <Pagination
              currentPage={pagination.page}
              totalPages={totalPages}
              totalItems={totalCases}
              pageSize={pagination.limit}
              onPageChange={handlePageChange}
            />
          </div>
        )}
      </Card>
    </div>
  )
}
